import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, X } from 'lucide-react';
import { Button } from "../ui/Button";
import { Plan } from '../utils/types';

const plans: Plan[] = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    billingPeriod: 'monthly',
    features: [
      '1 Trading Bot',
      'Basic Strategies',
      'Limited Exchange Support',
      'Community Support',
      'Basic Analytics',
      'Manual Trading',
    ],
    botLimit: 1,
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 29,
    billingPeriod: 'monthly',
    features: [
      '5 Trading Bots',
      'Advanced Strategies',
      'All Exchange Support',
      'TradingView Integration',
      'Email Support',
      'Comprehensive Analytics',
      'Strategy Testing',
      'Custom Risk Parameters',
    ],
    botLimit: 5,
    isPopular: true,
  },
  {
    id: 'ultimate',
    name: 'Ultimate',
    price: 99,
    billingPeriod: 'monthly',
    features: [
      'Unlimited Trading Bots',
      'Premium Strategies',
      'Priority API Access',
      'Priority Support',
      'Advanced Analytics',
      'White Glove Setup',
      'API Webhook Access',
      'Custom Strategy Development',
      'Dedicated Account Manager',
    ],
    botLimit: Infinity,
  },
];

export const Pricing: React.FC = () => {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');

  const toggleBillingPeriod = () => {
    setBillingPeriod(billingPeriod === 'monthly' ? 'yearly' : 'monthly');
  };

  return (
    <section id="pricing" className="py-20 bg-gray-50 dark:bg-gray-800">
      <div className="container mx-auto px-4">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">
            Simple, Transparent <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500">Pricing</span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto mb-8">
            Choose the plan that fits your trading needs. All plans include access to our core platform.
          </p>

          <div className="flex items-center justify-center mb-8">
            <span className={`mr-3 text-sm ${billingPeriod === 'monthly' ? 'text-gray-900 dark:text-white font-medium' : 'text-gray-500 dark:text-gray-400'}`}>
              Monthly
            </span>
            <button 
              onClick={toggleBillingPeriod}
              className="relative inline-flex h-6 w-12 items-center rounded-full bg-gray-200 dark:bg-gray-700"
            >
              <span className="sr-only">Toggle billing period</span>
              <span 
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition ${
                  billingPeriod === 'yearly' ? 'translate-x-7' : 'translate-x-1'
                }`} 
              />
            </button>
            <span className={`ml-3 text-sm ${billingPeriod === 'yearly' ? 'text-gray-900 dark:text-white font-medium' : 'text-gray-500 dark:text-gray-400'}`}>
              Yearly <span className="text-xs text-green-500 font-medium">(Save 20%)</span>
            </span>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <motion.div 
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className={`bg-white dark:bg-gray-900 rounded-2xl overflow-hidden shadow-lg ${
                plan.isPopular ? 'ring-2 ring-blue-500 dark:ring-blue-400' : 'border border-gray-200 dark:border-gray-700'
              }`}
            >
              {plan.isPopular && (
                <div className="bg-blue-500 text-white text-center py-2 text-sm font-medium">
                  MOST POPULAR
                </div>
              )}
              
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">{plan.name}</h3>
                
                <div className="mb-6">
                  <span className="text-4xl font-bold text-gray-900 dark:text-white">
                    ${billingPeriod === 'yearly' ? Math.floor(plan.price * 0.8) : plan.price}
                  </span>
                  {plan.price > 0 && (
                    <span className="text-gray-500 dark:text-gray-400 ml-2">
                      /{billingPeriod.replace('ly', '')}
                    </span>
                  )}
                </div>
                
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600 dark:text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Button 
                  variant={plan.isPopular ? 'primary' : 'outline'} 
                  className="w-full"
                  size="lg"
                >
                  {plan.price === 0 ? 'Get Started Free' : 'Subscribe Now'}
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};